import { useQuery } from '@tanstack/react-query';
import type { IDockviewPanelProps } from 'dockview-react';
import type { EChartsType } from 'echarts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Widget } from '../lib/api';
import { errorMessage, when } from '../lib/format';
import { unwrap } from './market';

/**
 * Dashboard widget tile (D10): renders a widget the agent or the user created — an ECharts option, a
 * table, a metric or text — optionally fed by a bound connector tool re-run on a timer. Specs are data
 * from an untrusted author (the agent can be prompt-injected): ECharts tooltips are forced to rich-text
 * (no HTML), the toolbox is dropped, and nothing in a spec is ever evaluated.
 */
export interface WidgetParams {
  widgetId: string;
}

/** `a.b[0].c` → value, or the whole document for an empty path. */
export function pick(doc: unknown, path: string | undefined): unknown {
  if (!path) return doc;
  let cur: unknown = doc;
  for (const key of path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

const cssVar = (n: string) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();

export function useWidget(id: string) {
  return useQuery({
    queryKey: ['widget', id],
    queryFn: () => api.widget(id),
    refetchInterval: 30_000,
    retry: 1,
  });
}

function useBoundData(w: Widget | undefined) {
  const src = w?.source;
  return useQuery({
    queryKey: [
      'widget-data',
      w?.id,
      src?.connector,
      src?.tool,
      JSON.stringify(src?.args ?? {}),
      src?.path,
    ],
    queryFn: async () => {
      if (!src) return undefined;
      const raw = unwrap(await api.invoke(src.connector, src.tool, src.args ?? {}));
      return pick(raw, src.path);
    },
    enabled: Boolean(src),
    refetchInterval: src ? Math.max(10, src.refreshSec ?? 60) * 1000 : false,
    retry: 1,
  });
}

/** Rows for the table kind: array of arrays, or array of objects (columns = keys of the first row). */
function tableFrom(spec: Record<string, unknown>, bound: unknown) {
  const data = bound ?? spec.rows;
  if (
    Array.isArray(data) &&
    data.length > 0 &&
    !Array.isArray(data[0]) &&
    typeof data[0] === 'object'
  ) {
    const columns = (spec.columns as string[] | undefined) ?? Object.keys(data[0] as object);
    return {
      columns,
      rows: data.map((r) => columns.map((c) => (r as Record<string, unknown>)[c])),
    };
  }
  return {
    columns: (spec.columns as string[] | undefined) ?? [],
    rows: (Array.isArray(data) ? data : []) as unknown[][],
  };
}

/** Row keys: position + a slice of the content (rows have no ids). */
const keyed = (rows: unknown[][]) =>
  rows.map((row, i) => ({ key: `${i}:${cell(row[0]).slice(0, 24)}`, row }));

const cell = (v: unknown) =>
  v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);

export function WidgetTile({ api: panel, params }: IDockviewPanelProps<WidgetParams>) {
  const w = useWidget(params.widgetId);
  const bound = useBoundData(w.data);
  useEffect(() => {
    if (w.data) panel.setTitle(w.data.title);
  }, [panel, w.data]);
  if (w.error)
    return (
      <div className="tile pad">
        <span className="error">{errorMessage(w.error)}</span>
      </div>
    );
  if (!w.data)
    return (
      <div className="tile pad">
        <span className="muted">loading…</span>
      </div>
    );
  const widget = w.data;
  const footer = (
    <div className="tile-foot muted">
      {widget.source
        ? `${widget.source.connector} · ${widget.source.tool} every ${widget.source.refreshSec ?? 60} s`
        : 'static'}
      {' · '}updated {when(widget.updatedAt)}
      {bound.error && <span className="error"> · {errorMessage(bound.error)}</span>}
    </div>
  );
  switch (widget.kind) {
    case 'echarts':
      return (
        <div className="tile widget-tile">
          <EChart option={widget.spec} data={bound.data} />
          {footer}
        </div>
      );
    case 'table': {
      const t = tableFrom(widget.spec, bound.data);
      return (
        <div className="tile widget-tile">
          <div className="widget-scroll">
            <table className="compact">
              {t.columns.length > 0 && (
                <thead>
                  <tr>
                    {t.columns.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {keyed(t.rows.slice(0, 500)).map(({ key, row }) => (
                  <tr key={key}>
                    {row.map((v, j) => (
                      <td key={t.columns[j] ?? `c${j}`}>{cell(v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {footer}
        </div>
      );
    }
    case 'metric': {
      const spec = widget.spec as { value?: unknown; unit?: string; delta?: number; note?: string };
      const value = bound.data !== undefined ? bound.data : spec.value;
      return (
        <div className="tile widget-tile">
          <div className="metric">
            <div className="metric-value">
              {cell(value)}
              {spec.unit && <span className="metric-unit">{spec.unit}</span>}
            </div>
            {typeof spec.delta === 'number' && (
              <div className={spec.delta >= 0 ? 'ok' : 'error'}>
                {spec.delta >= 0 ? '+' : ''}
                {spec.delta}
              </div>
            )}
            {spec.note && <div className="muted">{spec.note}</div>}
          </div>
          {footer}
        </div>
      );
    }
    default:
      return (
        <div className="tile widget-tile">
          <div className="widget-scroll widget-text">
            {cell((widget.spec as { text?: unknown }).text ?? bound.data)}
          </div>
          {footer}
        </div>
      );
  }
}

/** ECharts loaded on first use (it is ~1 MB); the option is sanitised, the bound data becomes dataset.source. */
function EChart({ option, data }: { option: Record<string, unknown>; data: unknown }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<EChartsType | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const applyRef = useRef<() => void>(() => {});

  useEffect(() => {
    let disposed = false;
    const node = el.current;
    if (!node) return;
    import('echarts').then((echarts) => {
      if (disposed || !node) return;
      chart.current = echarts.init(node, undefined, { renderer: 'canvas' });
      setErr(null);
      applyRef.current();
    });
    const ro = new ResizeObserver(() => chart.current?.resize());
    ro.observe(node);
    return () => {
      disposed = true;
      ro.disconnect();
      chart.current?.dispose();
      chart.current = null;
    };
  }, []);

  const apply = useCallback(() => {
    if (!chart.current) return;
    try {
      chart.current.setOption(safeOption(option, data), { notMerge: true });
      setErr(null);
    } catch (e) {
      setErr(errorMessage(e));
    }
  }, [option, data]);
  useEffect(() => {
    applyRef.current = apply;
    apply();
  }, [apply]);

  return (
    <div className="echart-box">
      <div ref={el} className="echart-canvas" />
      {err && <div className="error pad">{err}</div>}
    </div>
  );
}

/** Strip anything that could render HTML or run code; apply the workstation palette as defaults. */
export function safeOption(
  option: Record<string, unknown>,
  data: unknown,
): Record<string, unknown> {
  const o = JSON.parse(JSON.stringify(option)) as Record<string, unknown>;
  delete o.toolbox;
  const tooltip = (o.tooltip && typeof o.tooltip === 'object' ? o.tooltip : {}) as Record<
    string,
    unknown
  >;
  o.tooltip = { trigger: 'axis', ...tooltip, renderMode: 'richText' };
  if (data !== undefined && (Array.isArray(data) || typeof data === 'object'))
    o.dataset = { ...((o.dataset as Record<string, unknown>) ?? {}), source: data };
  const text = cssVar('--text') || '#e6e8ee';
  const muted = cssVar('--muted') || '#9aa3b2';
  const accent = cssVar('--accent') || '#4f8cff';
  o.backgroundColor = 'transparent';
  o.textStyle = { color: text, ...((o.textStyle as object) ?? {}) };
  o.color = (o.color as unknown[] | undefined) ?? [
    accent,
    cssVar('--ok') || '#3ecf8e',
    cssVar('--warn') || '#f5b942',
    cssVar('--danger') || '#ff5c5c',
    '#a78bfa',
    '#22d3ee',
  ];
  if (o.grid === undefined) o.grid = { left: 48, right: 16, top: 32, bottom: 32 };
  for (const axis of ['xAxis', 'yAxis']) {
    const a = o[axis];
    const fix = (x: Record<string, unknown>) => ({
      axisLine: { lineStyle: { color: muted } },
      axisLabel: { color: muted },
      splitLine: { lineStyle: { color: cssVar('--border') || '#2a2f3a' } },
      ...x,
    });
    if (Array.isArray(a)) o[axis] = a.map((x) => fix((x as Record<string, unknown>) ?? {}));
    else if (a && typeof a === 'object') o[axis] = fix(a as Record<string, unknown>);
  }
  return o;
}
